import type { LoomNativeHybridAsset } from "./loomNativeHybridAsset";
import { deriveLoomExactSourceFaceEvidence } from "./loomExactFaceOwnership";

interface OriginalVertexSource { kind: "original"; index: number }
interface EdgeVertexSource { kind: "edge"; a: number; b: number; t: number }
type VertexSource = OriginalVertexSource | EdgeVertexSource;

export interface LoomExplicitTrimParameterLines {
  wingChordwiseU: Uint32Array;
  wingSpanwiseV: Uint32Array;
  fuselageCircumferentialTheta: Uint32Array;
  fuselageAxialX: Uint32Array;
}

export interface LoomExplicitTrimTopology {
  boundaryEdgeCount: number;
  faceCornerParameters?: Float32Array;
  faceMirrored: Uint8Array;
  faceOwnerKinds: Uint8Array;
  faces: Uint32Array;
  mirrorVertex: Uint32Array;
  nonManifoldEdgeCount: number;
  parameterLines: LoomExplicitTrimParameterLines;
  referenceFaceNormals: Float32Array;
  seamRings: readonly Uint32Array[];
  sources: readonly VertexSource[];
  vertexCount: number;
}

interface PolygonVertex {
  index: number;
  uv?: readonly [number, number];
}

function faceNormals(positions: Float32Array, faces: Uint32Array): Float32Array {
  const normals = new Float32Array(faces.length);
  for (let offset = 0; offset < faces.length; offset += 3) {
    const a = faces[offset]! * 3; const b = faces[offset + 1]! * 3; const c = faces[offset + 2]! * 3;
    const abx = positions[b]! - positions[a]!; const aby = positions[b + 1]! - positions[a + 1]!;
    const abz = positions[b + 2]! - positions[a + 2]!; const acx = positions[c]! - positions[a]!;
    const acy = positions[c + 1]! - positions[a + 1]!; const acz = positions[c + 2]! - positions[a + 2]!;
    const nx = aby * acz - abz * acy; const ny = abz * acx - abx * acz;
    const nz = abx * acy - aby * acx; const length = Math.hypot(nx, ny, nz);
    if (length > 0) {
      normals[offset] = nx / length; normals[offset + 1] = ny / length; normals[offset + 2] = nz / length;
    }
  }
  return normals;
}

function topologyCounts(faces: Uint32Array, vertexCount: number): { boundary: number; nonManifold: number } {
  const counts = new Map<number, number>();
  for (let offset = 0; offset < faces.length; offset += 3) for (const [a, b] of ([
    [faces[offset]!, faces[offset + 1]!], [faces[offset + 1]!, faces[offset + 2]!],
    [faces[offset + 2]!, faces[offset]!],
  ] as Array<readonly [number, number]>)) {
    const key = Math.min(a, b) * vertexCount + Math.max(a, b);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return {
    boundary: [...counts.values()].filter((count) => count === 1).length,
    nonManifold: [...counts.values()].filter((count) => count > 2).length,
  };
}

export function loomExplicitTrimPositions(
  topology: LoomExplicitTrimTopology,
  half: Float32Array,
): Float32Array {
  const result = new Float32Array(topology.vertexCount * 3);
  topology.sources.forEach((source, index) => {
    if (source.kind === "original") result.set(half.subarray(source.index * 3, source.index * 3 + 3), index * 3);
    else for (let axis = 0; axis < 3; axis += 1) {
      result[index * 3 + axis] = half[source.a * 3 + axis]!
        + source.t * (half[source.b * 3 + axis]! - half[source.a * 3 + axis]!);
    }
    const mirror = topology.mirrorVertex[index]!;
    if (source.kind === "edge" || mirror === index) result[index * 3 + 1] = 0;
    if (mirror !== index) {
      result.set([result[index * 3]!, -result[index * 3 + 1]!, result[index * 3 + 2]!], mirror * 3);
    }
  });
  return result;
}

export function buildLoomExplicitTrimTopology(
  asset: LoomNativeHybridAsset,
  planeEpsilonM: number,
): LoomExplicitTrimTopology {
  const evidence = deriveLoomExactSourceFaceEvidence(asset, planeEpsilonM);
  const sources: VertexSource[] = [];
  const original = new Map<number, number>();
  const cuts = new Map<string, number>();
  const positions = asset.surface.positions;
  const planeY = (index: number): number => {
    const y = positions[index * 3 + 1]!;
    return Math.abs(y) <= planeEpsilonM ? 0 : y;
  };
  const sourceForOriginal = (index: number): number => {
    const cached = original.get(index);
    if (cached !== undefined) return cached;
    const result = sources.length; sources.push({ kind: "original", index }); original.set(index, result);
    return result;
  };
  const sourceForCut = (a: number, b: number): number => {
    const key = a < b ? `${a}:${b}` : `${b}:${a}`;
    const cached = cuts.get(key);
    if (cached !== undefined) return cached;
    const ay = planeY(a); const by = planeY(b);
    if (ay === 0 || by === 0 || (ay < 0) === (by < 0)) {
      throw new Error("explicit trim cut requires one true plane crossing");
    }
    const result = sources.length;
    sources.push({ kind: "edge", a, b, t: ay / (ay - by) }); cuts.set(key, result);
    return result;
  };
  const edgeIntersection = (a: number, b: number): { index: number; t: number } => {
    const ay = planeY(a); const by = planeY(b);
    if (ay === 0) return { index: sourceForOriginal(a), t: 0 };
    if (by === 0) return { index: sourceForOriginal(b), t: 1 };
    return { index: sourceForCut(a, b), t: ay / (ay - by) };
  };
  const clipSegment = (a: number, b: number): readonly number[] => {
    const aInside = planeY(a) >= 0;
    const bInside = planeY(b) >= 0;
    if (aInside && bInside) return [sourceForOriginal(a), sourceForOriginal(b)];
    if (!aInside && !bInside) return [];
    const cut = edgeIntersection(a, b).index;
    const result = aInside
      ? [sourceForOriginal(a), cut]
      : [cut, sourceForOriginal(b)];
    return result[0] === result[1] ? [] : result;
  };
  const halfFaces: number[] = []; const halfOwners: number[] = []; const halfUv: number[] = [];
  const exactUv = evidence.faceCornerParameters;
  for (let face = 0; face < asset.surface.faces.length / 3; face += 1) {
    const ids = [asset.surface.faces[face * 3]!, asset.surface.faces[face * 3 + 1]!, asset.surface.faces[face * 3 + 2]!];
    const uv = (corner: number): readonly [number, number] | undefined => exactUv
      ? [exactUv[(face * 3 + corner) * 2]!, exactUv[(face * 3 + corner) * 2 + 1]!] : undefined;
    const polygon: PolygonVertex[] = [];
    const pushPolygon = (vertex: PolygonVertex): void => {
      if (polygon[polygon.length - 1]?.index !== vertex.index) polygon.push(vertex);
    };
    for (let corner = 0; corner < 3; corner += 1) {
      const previous = (corner + 2) % 3; const a = ids[previous]!; const b = ids[corner]!;
      const aInside = planeY(a) >= 0;
      const bInside = planeY(b) >= 0;
      if (aInside !== bInside) {
        const intersection = edgeIntersection(a, b);
        const aUv = uv(previous); const bUv = uv(corner);
        pushPolygon({
          index: intersection.index,
          ...(aUv && bUv ? {
            uv: [
              aUv[0] + intersection.t * (bUv[0] - aUv[0]),
              aUv[1] + intersection.t * (bUv[1] - aUv[1]),
            ],
          } : {}),
        });
      }
      if (bInside) pushPolygon({
        index: sourceForOriginal(b), ...(uv(corner) ? { uv: uv(corner) } : {}),
      });
    }
    if (polygon.length > 1 && polygon[0]!.index === polygon[polygon.length - 1]!.index) polygon.pop();
    for (let index = 1; index + 1 < polygon.length; index += 1) {
      const triangle = [polygon[0]!, polygon[index]!, polygon[index + 1]!];
      halfFaces.push(...triangle.map(({ index: vertex }) => vertex));
      halfOwners.push(evidence.faceOwners[face]!);
      if (exactUv) for (const item of triangle) halfUv.push(...item.uv!);
    }
  }
  const halfLines = Object.fromEntries(Object.entries(asset.surface.parameterLines).map(([name, segments]) => {
    const clipped: number[] = [];
    for (let offset = 0; offset < segments.length; offset += 2) {
      clipped.push(...clipSegment(segments[offset]!, segments[offset + 1]!));
    }
    return [name, clipped];
  })) as Record<keyof LoomExplicitTrimParameterLines, number[]>;
  const mirrorVertex = new Uint32Array(sources.length); let vertexCount = sources.length;
  sources.forEach((source, index) => {
    const y = source.kind === "original" ? positions[source.index * 3 + 1]! : 0;
    mirrorVertex[index] = Math.abs(y) <= planeEpsilonM ? index : vertexCount++;
  });
  const faces = [...halfFaces]; const faceOwnerKinds = [...halfOwners];
  const faceMirrored = new Array<number>(halfOwners.length).fill(0); const faceCornerParameters = [...halfUv];
  for (let offset = 0; offset < halfFaces.length; offset += 3) {
    faces.push(mirrorVertex[halfFaces[offset]!]!, mirrorVertex[halfFaces[offset + 2]!]!, mirrorVertex[halfFaces[offset + 1]!]!);
    faceOwnerKinds.push(halfOwners[offset / 3]!); faceMirrored.push(1);
    if (exactUv) {
      const source = offset * 2;
      faceCornerParameters.push(
        halfUv[source]!, halfUv[source + 1]!, halfUv[source + 4]!, halfUv[source + 5]!,
        halfUv[source + 2]!, halfUv[source + 3]!,
      );
    }
  }
  const parameterLines = Object.fromEntries(Object.entries(halfLines).map(([name, segments]) => {
    const full = [...segments];
    for (let offset = 0; offset < segments.length; offset += 2) {
      full.push(mirrorVertex[segments[offset]!]!, mirrorVertex[segments[offset + 1]!]!);
    }
    return [name, new Uint32Array(full)];
  })) as unknown as LoomExplicitTrimParameterLines;
  const seamRings: Uint32Array[] = [];
  for (const sourceRing of evidence.seamVertexRings) {
    const starboard = new Uint32Array(sourceRing.length);
    for (let index = 0; index < starboard.length; index += 1) {
      const mapped = original.get(sourceRing[index]!);
      if (mapped === undefined) throw new Error("accepted shared seam is outside the retained explicit half");
      starboard[index] = mapped;
    }
    const port = new Uint32Array(starboard.length);
    for (let index = 0; index < starboard.length; index += 1) {
      port[index] = mirrorVertex[starboard[starboard.length - 1 - index]!]!;
    }
    seamRings.push(starboard, port);
  }
  const topology: LoomExplicitTrimTopology = {
    boundaryEdgeCount: 0, faceMirrored: new Uint8Array(faceMirrored),
    faceOwnerKinds: new Uint8Array(faceOwnerKinds), faces: new Uint32Array(faces), mirrorVertex,
    nonManifoldEdgeCount: 0, parameterLines, referenceFaceNormals: new Float32Array(faces.length),
    seamRings, sources, vertexCount,
    ...(exactUv ? { faceCornerParameters: new Float32Array(faceCornerParameters) } : {}),
  };
  const counts = topologyCounts(topology.faces, vertexCount);
  topology.boundaryEdgeCount = counts.boundary; topology.nonManifoldEdgeCount = counts.nonManifold;
  topology.referenceFaceNormals = faceNormals(loomExplicitTrimPositions(topology, positions), topology.faces);
  return topology;
}

export function invalidLoomExplicitTrimMeshReason(
  positions: Float32Array,
  topology: LoomExplicitTrimTopology,
): string | null {
  if (positions.length !== topology.vertexCount * 3 || !positions.every(Number.isFinite)
      || topology.boundaryEdgeCount !== 0 || topology.nonManifoldEdgeCount !== 0) {
    return "The symmetric surface has invalid coordinates or edge topology";
  }
  const normals = faceNormals(positions, topology.faces);
  for (let offset = 0; offset < normals.length; offset += 3) {
    const nx = normals[offset]!; const ny = normals[offset + 1]!; const nz = normals[offset + 2]!;
    if (nx === 0 && ny === 0 && nz === 0) return "The symmetric surface contains a zero-area triangle";
    const orientation = nx * topology.referenceFaceNormals[offset]!
      + ny * topology.referenceFaceNormals[offset + 1]!
      + nz * topology.referenceFaceNormals[offset + 2]!;
    if (!(orientation > 0)) return `The symmetric surface inverted triangle ${offset / 3}`;
  }
  const indicesValid = topology.faces.every((vertex) => vertex < topology.vertexCount)
    && (Object.values(topology.parameterLines) as Uint32Array[]).every((segments) => (
      segments.length % 2 === 0 && segments.every((vertex) => vertex < topology.vertexCount)
    ))
    && topology.seamRings.every((ring) => ring.every((vertex) => vertex < topology.vertexCount));
  if (!indicesValid) return "The symmetric surface contains an out-of-range topology index";
  if (topology.faceCornerParameters && (topology.faceCornerParameters.length !== topology.faces.length * 2
      || !topology.faceCornerParameters.every(Number.isFinite))) {
    return "The symmetric surface has misaligned exact face UV evidence";
  }
  return null;
}
